import React, { useMemo, useRef, useState } from "react";
import { IndianRupee, TrendingDown, Wallet, Hammer, Share2, Download, X, Sparkles } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { t } from "@/i18n";

// Approx wastage savings per breakdown — calibrated so total ≤ ₹50,000
const PLOT_OPTIONS = [
  { id: "1000", label: "1000", iron: 8000, cement: 6000, labour: 6000 },
  { id: "1500", label: "1500", iron: 12000, cement: 9000, labour: 9000 },
  { id: "2000", label: "2000", iron: 16000, cement: 13000, labour: 11000 },
  { id: "2500", label: "2500+", iron: 19000, cement: 16000, labour: 15000 },
];

const formatINR = (n) =>
  "₹" + Math.round(n).toLocaleString("en-IN");

// Simple roundRect path helper for canvas
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

const Bar = ({ percent, tone = "bad", label, value }) => (
  <div>
    <div className="flex items-center justify-between text-[12px] mb-1">
      <span className="font-semibold text-foreground">{label}</span>
      <span
        className={`font-bold ${
          tone === "good" ? "text-neem" : "text-red-600"
        }`}
      >
        {value}
      </span>
    </div>
    <div className="h-2.5 w-full rounded-full bg-sand-alt overflow-hidden">
      <div
        className={`h-full rounded-full transition-all duration-500 ${
          tone === "good"
            ? "bg-gradient-to-r from-neem to-[#7AAE7E]"
            : "bg-gradient-to-r from-red-500 to-red-400"
        }`}
        style={{ width: `${percent}%` }}
      />
    </div>
  </div>
);

const SavingsCalculator = ({ lang }) => {
  const [plotId, setPlotId] = useState("1500");
  const [shareOpen, setShareOpen] = useState(false);
  const [cardUrl, setCardUrl] = useState(null);
  const canvasRef = useRef(null);
  const plot = useMemo(
    () => PLOT_OPTIONS.find((p) => p.id === plotId) || PLOT_OPTIONS[1],
    [plotId]
  );
  const total = plot.iron + plot.cement + plot.labour;
  const wastedPct = 65;
  const savedPct = 100 - wastedPct;

  // Generate a personalised PNG savings card using Canvas
  const buildCard = () => {
    const W = 1080;
    const H = 1080;
    const c = canvasRef.current || document.createElement("canvas");
    c.width = W;
    c.height = H;
    const ctx = c.getContext("2d");

    // BG gradient (navy → deep navy)
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, "#0B2545");
    g.addColorStop(1, "#081A33");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);

    // Subtle gold dot pattern
    ctx.fillStyle = "rgba(212,165,55,0.10)";
    for (let y = 40; y < H; y += 48) {
      for (let x = 40; x < W; x += 48) {
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Top gold bar
    const topG = ctx.createLinearGradient(0, 0, W, 0);
    topG.addColorStop(0, "#B0871F");
    topG.addColorStop(0.5, "#F4D070");
    topG.addColorStop(1, "#B0871F");
    ctx.fillStyle = topG;
    ctx.fillRect(0, 0, W, 14);

    // Brand row
    ctx.fillStyle = "#D4A537";
    ctx.font = "bold 28px Hind, system-ui, sans-serif";
    ctx.fillText(t(lang, "share_card_kicker").toUpperCase(), 70, 110);

    // Headline
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 64px Hind, system-ui, sans-serif";
    const head1 = lang === "hi" ? "मेरा नक्शा से" : "With Mera Naksha";
    ctx.fillText(head1, 70, 200);
    ctx.fillStyle = "#F4D070";
    ctx.font = "900 86px Hind, system-ui, sans-serif";
    const head2 = t(lang, "share_card_total");
    ctx.fillText(head2, 70, 290);

    // Big amount in a card
    ctx.fillStyle = "rgba(255,255,255,0.06)";
    roundRect(ctx, 70, 340, W - 140, 360, 28);
    ctx.fill();
    ctx.strokeStyle = "rgba(212,165,55,0.4)";
    ctx.lineWidth = 2;
    roundRect(ctx, 70, 340, W - 140, 360, 28);
    ctx.stroke();

    ctx.fillStyle = "#D4A537";
    ctx.font = "bold 34px Hind, system-ui, sans-serif";
    ctx.fillText(
      t(lang, "savings_total").toUpperCase(),
      110,
      400
    );

    // Big rupee + amount
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "900 200px Hind, system-ui, sans-serif";
    const amount = "₹" + total.toLocaleString("en-IN");
    ctx.fillText(amount, 110, 580);

    ctx.fillStyle = "rgba(255,255,255,0.7)";
    ctx.font = "500 30px Hind, system-ui, sans-serif";
    ctx.fillText(
      t(lang, "share_card_for").replace("{X}", plot.label),
      110,
      640
    );

    // Breakdown row
    const items = [
      { label: t(lang, "savings_iron"), value: plot.iron },
      { label: t(lang, "savings_cement"), value: plot.cement },
      { label: t(lang, "savings_labour"), value: plot.labour },
    ];
    const bx = 110;
    let by = 770;
    items.forEach((it, i) => {
      const x = bx + i * 300;
      ctx.fillStyle = "rgba(255,255,255,0.08)";
      roundRect(ctx, x, by, 280, 110, 18);
      ctx.fill();
      ctx.strokeStyle = "rgba(212,165,55,0.25)";
      ctx.lineWidth = 1.5;
      roundRect(ctx, x, by, 280, 110, 18);
      ctx.stroke();
      ctx.fillStyle = "#D4A537";
      ctx.font = "bold 20px Hind, system-ui, sans-serif";
      ctx.fillText(it.label.toUpperCase(), x + 22, by + 38);
      ctx.fillStyle = "#FFFFFF";
      ctx.font = "900 36px Hind, system-ui, sans-serif";
      ctx.fillText("₹" + it.value.toLocaleString("en-IN"), x + 22, by + 84);
    });

    // CTA footer
    ctx.fillStyle = "#25D366";
    roundRect(ctx, 70, H - 150, W - 140, 90, 24);
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 36px Hind, system-ui, sans-serif";
    ctx.fillText("✓ " + t(lang, "share_card_cta"), 110, H - 92);

    // URL line
    ctx.fillStyle = "rgba(255,255,255,0.6)";
    ctx.font = "500 24px Hind, system-ui, sans-serif";
    ctx.fillText(getShareUrl(), 70, H - 30);

    canvasRef.current = c;
    return c.toDataURL("image/png");
  };

  const getShareUrl = () =>
    typeof window !== "undefined" ? window.location.origin + "/" : "https://meranaksha.com/";

  const openShare = () => {
    const url = buildCard();
    setCardUrl(url);
    setShareOpen(true);
  };

  const downloadCard = () => {
    if (!cardUrl) return;
    const a = document.createElement("a");
    a.href = cardUrl;
    a.download = `mera-naksha-savings-${plot.label}sqft.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const shareOnWhatsApp = () => {
    const msg =
      t(lang, "share_msg").replace("{X}", total.toLocaleString("en-IN")) +
      " " +
      getShareUrl();
    window.open(
      `https://wa.me/?text=${encodeURIComponent(msg)}`,
      "_blank",
      "noopener"
    );
  };

  return (
    <section className="mt-12" data-testid="savings-section">
      <div className="rounded-3xl bg-navy text-white p-5 sm:p-7 shadow-[0_30px_60px_-30px_rgba(11,37,69,0.65)] border border-navy-600">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold/20 text-gold text-xs font-bold tracking-wide border border-gold/30">
          <TrendingDown className="h-3.5 w-3.5" />
          {t(lang, "savings_eyebrow")}
        </div>
        <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white text-balance">
          {t(lang, "savings_title")}{" "}
          <span className="gold-shimmer animate-shimmer">
            {t(lang, "savings_title_hl")}
          </span>
        </h2>
        <p className="mt-2 text-[14.5px] text-white/75 leading-relaxed">
          {t(lang, "savings_sub")}
        </p>

        {/* Brick-saver visual — shows the "65% bricks wasted" reality
            (left) vs. the Mera Naksha optimised stack (right). High-impact
            single image, dark-friendly. */}
        <div
          className="mt-5 rounded-2xl overflow-hidden border-2 border-gold/30 bg-navy-600/30 relative"
          data-testid="brick-saver-visual"
        >
          <img
            src="https://static.prod-images.emergentagent.com/jobs/b351a7fb-087a-4fca-96f5-8e5804986d90/images/brick_saver_visual.png"
            alt={lang === "hi" ? "Bricks बचाओ — Mera Naksha के साथ" : "Brick Saver — with Mera Naksha"}
            loading="lazy"
            className="w-full h-auto block"
            onError={(e) => {
              // Hide the wrapper if the image 404s so we don't show a broken
              // placeholder. The calculator UI below is the substantive proof.
              e.currentTarget.parentElement.style.display = "none";
            }}
          />
          <div className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-navy/80 backdrop-blur-sm border border-gold/30 text-[10.5px] font-extrabold text-gold tracking-wider uppercase">
            <Sparkles className="h-3 w-3" />
            {lang === "hi" ? "Brick Saver" : "Brick Saver"}
          </div>
        </div>

        {/* Plot selector */}
        <div className="mt-5">
          <div className="text-[11px] uppercase tracking-[0.18em] text-gold font-bold mb-2">
            {t(lang, "savings_choose_plot")}
          </div>
          <div className="grid grid-cols-4 gap-2" role="tablist">
            {PLOT_OPTIONS.map((p) => {
              const active = plotId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => setPlotId(p.id)}
                  data-testid={`savings-plot-${p.id}`}
                  className={`min-h-[56px] rounded-xl px-2 py-2 text-center transition-all active:scale-[0.98] border-2 ${
                    active
                      ? "bg-gold text-navy border-gold shadow-[0_8px_18px_rgba(212,165,55,0.35)]"
                      : "bg-navy-600/60 text-white/85 border-navy-600 hover:border-gold/60"
                  }`}
                >
                  <div className="text-base font-bold leading-none">
                    {p.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider mt-1 opacity-80">
                    {t(lang, "sqft")}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Big savings number */}
        <div
          className="mt-5 rounded-2xl bg-white/5 border border-white/10 p-4 sm:p-5 backdrop-blur"
          data-testid="savings-total-card"
          key={plotId}
        >
          <div className="flex items-end justify-between gap-4">
            <div className="animate-count-up">
              <div className="text-[12px] uppercase tracking-[0.18em] text-gold font-bold">
                {t(lang, "savings_total")}
              </div>
              <div className="mt-1 flex items-baseline gap-1">
                <IndianRupee className="h-7 w-7 text-gold" strokeWidth={2.5} />
                <span className="text-4xl sm:text-5xl font-extrabold tracking-tight text-white tabular-nums">
                  {Math.round(total).toLocaleString("en-IN")}
                </span>
              </div>
              <div className="text-[12px] text-white/60 mt-1">
                {lang === "hi"
                  ? `${plot.label} ${t(lang, "sqft")} पर अनुमानित बचत`
                  : `Estimated savings for ${plot.label} ${t(lang, "sqft")}`}
              </div>
            </div>
            <div className="hidden sm:flex h-16 w-16 rounded-2xl bg-gold/15 border border-gold/30 items-center justify-center">
              <Wallet className="h-8 w-8 text-gold" />
            </div>
          </div>

          {/* Comparison bars */}
          <div className="mt-5 grid sm:grid-cols-2 gap-4">
            <div className="rounded-xl bg-red-500/10 border border-red-400/30 p-3">
              <div className="text-[11px] uppercase tracking-wider text-red-300 font-bold mb-1">
                {t(lang, "savings_without")}
              </div>
              <div className="text-white/85 text-[13px]">
                {t(lang, "savings_wasted")}: ~{wastedPct}%
              </div>
              <div className="mt-2 h-2 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-red-500 to-red-400"
                  style={{ width: `${wastedPct}%` }}
                />
              </div>
            </div>
            <div className="rounded-xl bg-neem/15 border border-neem/40 p-3">
              <div className="text-[11px] uppercase tracking-wider text-emerald-200 font-bold mb-1">
                {t(lang, "savings_with")}
              </div>
              <div className="text-white/85 text-[13px]">
                {t(lang, "savings_saved")}: ~{savedPct}%
              </div>
              <div className="mt-2 h-2 w-full bg-white/10 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300"
                  style={{ width: `${savedPct}%` }}
                />
              </div>
            </div>
          </div>

          {/* Breakdown */}
          <div className="mt-5 space-y-3 bg-white rounded-xl p-4">
            <div className="flex items-center gap-2 text-foreground font-bold text-sm">
              <Hammer className="h-4 w-4 text-navy" />
              {lang === "hi" ? "बचत का ब्यौरा" : "Where you save"}
            </div>
            <Bar
              label={t(lang, "savings_iron")}
              value={formatINR(plot.iron)}
              percent={(plot.iron / 19000) * 100}
              tone="good"
            />
            <Bar
              label={t(lang, "savings_cement")}
              value={formatINR(plot.cement)}
              percent={(plot.cement / 16000) * 100}
              tone="good"
            />
            <Bar
              label={t(lang, "savings_labour")}
              value={formatINR(plot.labour)}
              percent={(plot.labour / 15000) * 100}
              tone="good"
            />
          </div>

          <div className="mt-3 text-[11px] text-white/50">
            {t(lang, "savings_disclaimer")}
          </div>

          {/* Share my savings */}
          <button
            onClick={openShare}
            data-testid="share-savings-btn"
            className="mt-5 w-full min-h-[60px] rounded-2xl bg-gold text-navy font-extrabold text-[15px] flex items-center justify-center gap-2 shadow-[0_12px_28px_rgba(212,165,55,0.35)] active:scale-[0.99] hover:bg-gold-dark hover:text-white transition-colors"
          >
            <Share2 className="h-5 w-5" />
            {t(lang, "share_btn")}
          </button>
        </div>
      </div>

      {shareOpen && (
        <div
          className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShareOpen(false)}
          data-testid="share-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl border-2 border-gold animate-fade-up"
          >
            <div className="bg-navy text-white px-5 py-3 flex items-center justify-between">
              <div className="text-sm font-bold tracking-wider uppercase text-gold">
                {t(lang, "share_btn")}
              </div>
              <button
                onClick={() => setShareOpen(false)}
                data-testid="share-close"
                className="h-8 w-8 rounded-full bg-white/10 grid place-items-center text-white"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            {cardUrl && (
              <img
                src={cardUrl}
                alt="Mera Naksha savings card"
                data-testid="share-card-preview"
                className="w-full block"
              />
            )}
            <div className="p-4 grid grid-cols-2 gap-2">
              <button
                onClick={downloadCard}
                data-testid="share-download-btn"
                className="min-h-[52px] rounded-xl bg-navy text-gold font-bold text-[14px] flex items-center justify-center gap-2 active:scale-[0.98]"
              >
                <Download className="h-4 w-4" />
                {t(lang, "share_download")}
              </button>
              <button
                onClick={shareOnWhatsApp}
                data-testid="share-whatsapp-btn"
                className="min-h-[52px] rounded-xl bg-whatsapp text-white font-bold text-[14px] flex items-center justify-center gap-2 active:scale-[0.98] shadow-[0_8px_20px_rgba(37,211,102,0.35)]"
              >
                <FaWhatsapp className="h-4 w-4" />
                {t(lang, "share_wa")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SavingsCalculator;
